import { Container } from "@material-ui/core";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { getUserName } from "@saleor/misc";
import React from "react";

import { useAssignInfo } from "../../misc";
import {
  useAccountAssignBrand,
  useAccountAssignMall,
  useAccountAssignTenant
} from "../../queries";
import { AccountSettingDetails_user } from "../../types/AccountSettingDetails";
import { AccountInfo } from "../AccountInfo";
import { GeneralInfo } from "../GeneralInfo";
import UserInformation from "../UserInformation";
import { hasNoError, useAccountValidation } from "./Validations";
export interface AccountSettingDetailsPageFormData {
  contactNo: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface AccountSettingDetailsPageProp {
  user: AccountSettingDetails_user;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  canChangePassword: boolean;
  onChangePassword: () => void;
  onSubmit: (data: AccountSettingDetailsPageFormData) => SubmitPromise;
}

export const AccountSettingDetailsPage: React.FC<AccountSettingDetailsPageProp> = ({
  user,
  onSubmit,
  saveButtonBarState,
  onBack,
  canChangePassword,
  onChangePassword,
  disabled
}) => {
  const userInfo = useAssignInfo(user);

  const { data: accountBrandAssignmentData } = useAccountAssignBrand({
    displayLoader: true,
    skip: !userInfo?.brand,
    variables: {
      id: userInfo?.brand
    }
  });
  const { data: accountMallAssignmentData } = useAccountAssignMall({
    displayLoader: true,
    skip: !userInfo?.mall,
    variables: {
      id: userInfo?.mall
    }
  });
  const { data: accountTenantAssignmentData } = useAccountAssignTenant({
    displayLoader: true,
    skip: !userInfo?.tenant,
    variables: {
      id: userInfo?.tenant
    }
  });
  const mallName = accountMallAssignmentData?.mall?.name || "-";
  const tenantName = accountTenantAssignmentData?.tenant?.storeName || "-";
  const brandName =
    accountTenantAssignmentData?.tenant?.brand?.name ||
    accountBrandAssignmentData?.brand?.name ||
    "-";
  const companyNameByBrandId =
    accountTenantAssignmentData?.tenant?.brand?.legalName ||
    accountBrandAssignmentData?.brand?.legalName ||
    "-";

  const initialForm: AccountSettingDetailsPageFormData = {
    contactNo: user?.contactNo || "",
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || ""
  };

  const [validationError, setValidationError] = React.useState({
    contactNo: null,
    firstName: null,
    lastName: null
  });

  const handleSubmit = event => {
    setValidationError({
      contactNo: null,
      firstName: null,
      lastName: null
    });
    const validate = useAccountValidation(event);
    if (hasNoError(validate)) {
      onSubmit(event);
    } else {
      setValidationError(validate);
    }
  };

  return (
    <>
      <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
        {({ data, change, submit, hasChanged }) => (
          <Container>
            <PageHeader title={getUserName(user)}></PageHeader>
            <Grid>
              <UserInformation data={data} user={user} />
              <CardSpacer />

              <GeneralInfo
                data={data}
                disabled={disabled}
                localError={validationError}
                onChange={change}
                userType={userInfo?.userType}
              />

              <CardSpacer />

              <AccountInfo
                dateJoined={user?.dateJoined}
                mall={mallName}
                company={companyNameByBrandId}
                brand={brandName}
                tenant={tenantName}
                userType={userInfo?.userType}
                disabled={disabled}
                onChange={change}
                onChangePassword={onChangePassword}
                canChangePassword={canChangePassword}
              />
            </Grid>

            <div>
              <SaveButtonBar
                state={saveButtonBarState}
                onCancel={onBack}
                onSave={submit}
                disabled={disabled || !hasChanged}
              />
            </div>
          </Container>
        )}
      </Form>
    </>
  );
};
export default AccountSettingDetailsPage;
